import type { SyntheticEvent } from 'react';
import { T, useGT } from 'gt-react';
import {
  AlarmIcon,
  ArrowSquareOutIcon,
  DotsThreeVerticalIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { isSnoozableTaskStatus } from './taskSnooze';
import { useCanDeleteTask } from './$taskId/taskDeletion';

/** The fields of a queue or inbox task row that the row menu needs. */
export type TaskRowMenuTask = {
  id: string;
  status: string;
  anchorEntityType?: string | null;
};

/**
 * Secondary per-row task actions, shared by the `⋮` button and the row's
 * right-click menu so both always offer the same items.
 */
export function TaskRowMenuItems({
  task,
  onSnooze,
  onDelete,
}: {
  task: TaskRowMenuTask;
  onSnooze?: () => void;
  onDelete?: () => void;
}) {
  const canDelete = useCanDeleteTask(task);
  return (
    <>
      <DropdownMenuItem
        onClick={() => window.open(`/tasks/${task.id}`, '_blank', 'noopener')}
      >
        <ArrowSquareOutIcon className="size-4" />
        <T context="task row menu item that opens the task in a new browser tab">
          Open in new tab
        </T>
      </DropdownMenuItem>
      {onSnooze && isSnoozableTaskStatus(task.status) && (
        <DropdownMenuItem onClick={onSnooze}>
          <AlarmIcon className="size-4" />
          <T context="task row menu item that opens the snooze dialog">
            Snooze…
          </T>
        </DropdownMenuItem>
      )}
      {onDelete && canDelete && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={onDelete}>
            <TrashIcon className="size-4" />
            <T context="task row menu item that opens confirmation to delete the task">
              Delete…
            </T>
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}

function stopRowEvent(event: SyntheticEvent) {
  event.stopPropagation();
}

/**
 * The `⋮` button at the end of a task row. Rows navigate on click (stretch link
 * or row handler), and React bubbles events from the portaled menu through the
 * component tree, so the wrapper isolates clicks, keys and right-clicks.
 */
export function TaskRowMenuButton({
  task,
  onSnooze,
  onDelete,
}: {
  task: TaskRowMenuTask;
  onSnooze?: () => void;
  onDelete?: () => void;
}) {
  const gt = useGT();
  return (
    <span
      role="presentation"
      className="relative z-10 inline-flex"
      onClick={stopRowEvent}
      onKeyDown={stopRowEvent}
      onContextMenu={stopRowEvent}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground"
            aria-label={gt('More task actions', {
              context: 'aria label for the overflow menu on a task row',
            })}
          >
            <DotsThreeVerticalIcon weight="bold" className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <TaskRowMenuItems
            task={task}
            onSnooze={onSnooze}
            onDelete={onDelete}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
